import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import App from './App';
import CacheManager, { CacheManager as CacheManagerClass } from './utils/CacheManager';
import ImageCacheManager from './utils/ImageCacheManager';
import ImagePreloader from './utils/ImagePreloader';

// Expose cache management functions globally for debugging
declare global {
  interface Window {
    grandline: {
      clearCache: () => Promise<void>;
      getCacheVersion: () => string;
      cacheManager: typeof CacheManager;
      imageCache: typeof ImageCacheManager;
      imagePreloader: typeof ImagePreloader;
      getCacheStats: () => Promise<void>;
      getImageCacheStats: () => Promise<void>;
      preloadImages: () => Promise<void>;
      clearImageCache: () => Promise<void>;
    };
  }
}

window.grandline = {
  clearCache: async () => {
    await CacheManagerClass.forceResetCache();
    console.log('💡 Tip: Reload the page to reinitialize with fresh cache');
  },
  getCacheVersion: () => {
    const version = CacheManagerClass.getCacheVersion();
    console.log(`📦 Cache version: ${version}`);
    return version;
  },
  cacheManager: CacheManager,
  imageCache: ImageCacheManager,
  imagePreloader: ImagePreloader,
  getCacheStats: async () => {
    const stats = await CacheManager.getCombinedStats();
    console.log('📊 Cache Statistics:');
    console.table(stats);
  },
  getImageCacheStats: async () => {
    const stats = await ImageCacheManager.getFormattedStats();
    console.log(stats);
  },
  preloadImages: async () => {
    console.log('🚀 Starting image preload...');
    await ImagePreloader.preloadAll();
    console.log('✅ Image preload complete');
  },
  clearImageCache: async () => {
    await ImageCacheManager.clear();
    console.log('🗑️ Image cache cleared');
  },
};

// Initialize image preloader
console.log('🎮 Grand Line loaded!');
console.log('🔄 Initializing image cache...');
ImagePreloader.preloadEssentials().then(() => {
  console.log('✅ Essential images cached');
  // Preload common items in background
  ImagePreloader.preloadCommonItems().catch(err => 
    console.warn('Failed to preload common items:', err)
  );
});

console.log('💡 Debug commands available:');
console.log('   window.grandline.clearCache() - Force clear all cache');
console.log('   window.grandline.getCacheVersion() - Check cache version');
console.log('   window.grandline.getCacheStats() - View cache statistics');
console.log('   window.grandline.getImageCacheStats() - View image cache stats');
console.log('   window.grandline.preloadImages() - Preload all images');
console.log('   window.grandline.clearImageCache() - Clear image cache only');

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
